import type { BaseProduct } from '@bulknutrients/bulk-shared-lib/interfaces/Product';
import { getAllProductsExp } from '@bulknutrients/bulk-shared-lib/lib/sanity/productData';
import { AlphabeticScroll } from '@components/AlphabeticalScroll/AlphabeticalScroll';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { QuickAddButton } from '@components/Button';
import { Container } from '@components/Container';
import { StandardLayout } from '@components/Layouts/StandardLayout';
import type { GetStaticProps } from 'next';
import Head from 'next/head';
import Link from 'next/link';
import type { ReactElement } from 'react';

/**
 * Page for the express ordering / all products on one page. Provides a 1 page
 * stop for adding any product to their cart
 */
export default function ExpressOrderPage({
  products,
}: {
  products: BaseProduct[];
}): ReactElement {
  return (
    <>
      <Head>
        <title>Express Ordering at Bulk - All Products on one page</title>
        <meta
          name="title"
          content="Express Ordering at Bulk - All Products on one page"
        />
        <meta
          name="description"
          content="Order Bulk Nutrients supplements quicker online in Australia on this single page. Add any product to cart from a list of products available for purchase"
        />
        <link
          rel="canonical"
          href={`${process.env.NEXT_PUBLIC_SERVER_URL}/express`}
        />
      </Head>
      <Container>
        <Breadcrumbs
          crumbs={[
            { title: 'Home', path: '/' },
            { title: 'Express Ordering', path: '/express' },
          ]}
        />
      </Container>
      <Container as="main" id="main-content">
        <div className="mx-auto my-0 max-w-narrow-page justify-self-center px-0 py-5">
          <h1>Express Ordering</h1>
          <p>
            Great work on finding our one-stop shop page. From this page you can
            quickly find any of our products and add them directly to your cart.
          </p>
        </div>
      </Container>
      <section className="mx-auto grid max-w-max-width grid-cols-[1fr_0.2fr] grid-rows-1 pl-5 pr-0 [grid-template-areas:'products_alpha'] sm:grid-cols-[0.2fr_1fr] sm:p-8 sm:[grid-template-areas:'alpha_products']">
        <div className="text-center [grid-area:alpha]">
          {products && (
            <AlphabeticScroll
              data={products}
              displayAttribute="name"
              idAttribute="entityId"
            />
          )}
        </div>
        <div className="flex flex-col [grid-area:products]">
          {products
            ? products.map((product, i: number) => (
                <>
                  <div
                    key={`express-page-product-${i}`}
                    className="inline-flex flex-col pt-8"
                  >
                    <Link
                      prefetch={process.env.NEXT_PUBLIC_PREFETCH === 'true'}
                      href={product.slug}
                      className="w-fit text-inherit no-underline"
                    >
                      <h4
                        id={product.entityId.toString()}
                        className="cursor-pointer font-secondary text-3xl font-semibold uppercase text-grey-dark underline decoration-grey-mid decoration-1 underline-offset-[0.1em] duration-500 hover:text-black hover:decoration-orange hover:decoration-3 hover:duration-200 dark:text-white dark:hover:text-grey-mid"
                      >
                        {product.name}
                      </h4>
                    </Link>
                    <p className="mx-0 my-2">{product.shortDescription}</p>
                    <div className="w-fit">
                      {product.discontinued ? null : (
                        <QuickAddButton
                          product={product}
                          className="hover:scale-120"
                          data-umami-event-location="expressPage"
                        />
                      )}
                    </div>
                  </div>
                </>
              ))
            : null}
        </div>
      </section>
    </>
  );
}

/* 
Set the layout of the express order page, place within the typical standard layout as there
isn't anything special with it 
*/
ExpressOrderPage.getLayout = function getLayout(page: ReactElement) {
  return <StandardLayout>{page}</StandardLayout>;
};

/*
Data for the express products order page is tailored specifically, as to cut down on the network payload
*/
export const getStaticProps: GetStaticProps = async () => {
  /* Get all the products queried specifically for the express page */
  let products = [] as Array<BaseProduct>;
  try {
    products = await getAllProductsExp();
  } catch (err) {
    console.error('Error fetching express products', err);
  }

  return {
    props: {
      products,
    },
  };
};
